import { Component } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import equals from 'ramda/src/equals';
import find from 'ramda/src/find';
import isNil from 'ramda/src/isNil';
import keys from 'ramda/src/keys';
import propEq from 'ramda/src/propEq';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import SaveActualEmployeeReport from '@components/SaveActualEmployeeReport';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import ManageActions from '~/actions/manage';
import { ScorecardsTabs } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';
import AverageScore from './AverageScore';
import EmployeeList from './EmployeeList';
import OrgChartEmployeeReport from './OrgChartEmployeeReport';
import { PerformanceReviewPdf } from './PerformanceReviewPdf';

const moment = extendMoment(originalMoment);

class ScorecardList extends Component {
    state = {
        companyId: equals([-1], this.props.companyId) ? this.props.companies.map((e) => e.id) : this.props.companyId,
        userId: this.props.employees.map((e) => e.id),
        dateStart: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
        dateEnd: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
        page: 1,
        perPage: 25,
        activeKey: [],
        column: [],
        active: queryString.parse(this.props.location?.search || '')?.active || 'chart',
    };

    componentDidMount() {
        const { companyId, page, perPage, dateStart, dateEnd } = this.state;
        this.props.getActualsReport({ companyId, dateStart }, false);
        // Run in background since the default tab is Org
        this.handleFetch(false, companyId, null, page, perPage, dateStart, dateEnd);
    }

    handleTab = (e) => {
        const { active } = this.state;
        if (e.name === active) return;
        this.props.navigate({ search: `?active=${e.name}` });
        this.setState({ active: e.name });
    };

    handleDate = (e) => {
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        const { companyId, userId, page, perPage } = this.state;
        this.handleFetch(true, companyId, userId, page, perPage, dateStart, dateEnd);
        this.props.getActualsReport({ companyId, dateStart });
        this.setState({ dateStart, dateEnd });
    };

    handleDetail = (scorecard) => (e) => {
        e.stopPropagation();
        if (scorecard) {
            const { dateStart, dateEnd } = this.state;
            const date = moment.range(dateStart, dateEnd);
            const payload = { type: 'Scorecard Detail', data: { before: { scorecard, date } } };
            this.props.toggleModal(payload);
        }
    };

    handleFetch = (loading = true, companyIds, userIds, page, perPage, dateStart, dateEnd, order = 'name', sort = 'ASC') => {
        let _companyIds, _userIds;
        if (companyIds[0] < 0) _companyIds = this.props.companies.map((item) => item.id);
        else _companyIds = companyIds;

        if (userIds === null || userIds.length === 0 || userIds[0] < 0) _userIds = null;
        else _userIds = userIds;

        const payload = {
            companyId: _companyIds,
            userId: _userIds,
            page,
            perPage,
            dateStart,
            dateEnd,
            order,
            sort,
        };
        this.props.fetchCompanyReviews(payload, loading);
        this.props.fetchCompanyReviewsStats(payload, loading);
    };

    handleFilter = (type, e) => {
        const { companyId, perPage, page, dateStart, dateEnd } = this.state;
        if (type === 'company') {
            const companyIds = e.map((item) => item.id);
            this.props.getActualsReport({ companyId: companyIds, dateStart });
            this.handleFetch(true, companyIds, [], page, perPage, dateStart, dateEnd);
            this.setState({ companyId: companyIds });
        } else if (type === 'employee') {
            const userIds = e.map((item) => item.id);
            this.handleFetch(true, companyId, userIds, page, perPage, dateStart, dateEnd);
            this.setState({ userId: userIds });
        }
    };

    handlePage = (page) => {
        const { companyId, userId, perPage, dateStart, dateEnd } = this.state;
        this.handleFetch(true, companyId, userId, page, perPage, dateStart, dateEnd);
        this.setState({ page });
    };

    handlePer = (perPage) => {
        const { companyId, userId, page, dateStart, dateEnd } = this.state;
        this.handleFetch(true, companyId, userId, page, perPage, dateStart, dateEnd);
        this.setState({ perPage });
    };

    handleSelectMenu = (data) => (e) => {
        const { employees } = this.props;
        const { companyId, userId, page, perPage, dateStart, dateEnd, active } = this.state;
        const { scorecards } = data;
        const user = data?.user ?? find(propEq('id', data?.user?.id), employees);
        switch (e) {
            case 'save actuals':
                this.props.toggleModal({
                    type: 'Save Actuals',
                    data: {
                        before: {
                            user: { ...user, company_id: companyId },
                            date: moment(dateStart).format('YYYY-MM-DD'),
                            after:
                                active !== 'employees'
                                    ? {
                                          type: 'GETSAVEACTUALSREPORT_REQUEST',
                                          payload: {
                                              companyId,
                                              dateStart,
                                          },
                                      }
                                    : {
                                          type: 'COMPANYPERFORMANCEREVIEWS_REQUEST',
                                          payload: {
                                              companyId,
                                              userId,
                                              page,
                                              perPage,
                                              dateStart,
                                              dateEnd,
                                              order: 'name',
                                              sort: 'ASC',
                                          },
                                      },
                        },
                    },
                });
                break;

            case 'save daily actuals':
                this.props.toggleModal({
                    type: 'Save Daily Actuals',
                    data: {
                        before: {
                            user: { ...user, company_id: companyId },
                            date: moment(dateStart).format('YYYY-MM-DD'),
                        },
                    },
                });
                break;

            case 'employee profile':
                {
                    if (user.community_user?.entity?.slug) {
                        const payload = { userId: user.community_user?.id };
                        this.props.getUser(payload, `/${user.community_user?.entity?.slug}/about?user_id=${user.community_user_id}`);
                    } else {
                        toast.error(`The User ${user?.profile?.first_name} ${user?.profile?.last_name} does not have a community profile.`, { position: toast.POSITION.TOP_RIGHT });
                    }
                }
                break;

            case 'start review':
                this.handleReview(data);
                break;

            case 'assign scorecard':
                this.handleAssign(data);
                break;

            case 'performance report':
                {
                    const userId = user.id;
                    const payload = { userId, companyId, dateStart, dateEnd };
                    this.props.fetchPersonalReviews(payload, `/hcm/report-performance/${userId}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`);
                }
                break;

            case 'preview scorecard': {
                const date = moment.range(dateStart, dateEnd);
                const payload = {
                    type: 'Scorecard Detail',
                    data: { before: { scorecard: scorecards[0], date } },
                };
                this.props.toggleModal(payload);
                break;
            }

            case 'unassign scorecard': {
                const after =
                    active !== 'employees'
                        ? {
                              type: 'GETSAVEACTUALSREPORT_REQUEST',
                              payload: {
                                  companyId,
                                  dateStart,
                              },
                          }
                        : {
                              type: 'COMPANYPERFORMANCEREVIEWS_REQUEST',
                              payload: {
                                  companyId,
                                  userId,
                                  page,
                                  perPage,
                                  dateStart,
                                  dateEnd,
                                  order: 'name',
                                  sort: 'ASC',
                              },
                          };

                this.props.toggleModal({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Unassign Scorecard',
                            body: 'This will permanently delete the scorecard from your assignments.  Are you sure?',
                        },
                    },
                    callBack: {
                        onYes: () => {
                            this.props.deleteScorecard({ scorecard: { id: scorecards[0].id } }, after);
                        },
                    },
                });
                break;
            }

            default:
                break;
        }
    };

    handleReview = (e) => {
        const { dateStart, dateEnd } = this.state;
        if (e.scorecards.length == 0) {
            if (this.props.isManager) this.handleAssign(e);
            else {
                toast.warn('Please see your Admin or Manager and assign a scorecard to complete a review.', {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        } else {
            const userId = e.user.id;
            const companyId = e.scorecards[0].company_id || Number(keys(e.user.data.company)[0]);
            const payload = { companyId, userId, dateStart, dateEnd };
            this.props.fetchPersonalReviews(payload, `/hcm/performance-reviews/${userId}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`);
        }
    };

    handlePerformanceReport = (item) => (e) => {
        e.stopPropagation();
        const { dateStart, dateEnd } = this.state;
        const userId = item.user.id;
        const companyId = item.scorecards.length > 0 && item.scorecards[0].company_id ? item.scorecards[0].company_id : Number(keys(item.user.data.company)[0]);
        const payload = { companyId, userId, dateStart, dateEnd };
        this.props.fetchPersonalReviews(payload, `/hcm/report-performance/${userId}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`);
    };

    handleUnassignScorecard = (e) => {
        if (isNil(e)) return;
        let { companyId } = this.state;
        const { dateStart } = this.state;

        if (companyId[0] < 0) companyId = this.props.companies.map((item) => item.id);
        const payload = { scorecard: { id: e.id } };
        const after = { type: 'GETSAVEACTUALSREPORT_REQUEST', payload: { companyId, dateStart } };

        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Unassign Scorecard',
                    body: 'This will permanently delete the scorecard from your assignments.  Are you sure?',
                },
            },
            callBack: {
                onYes: () => {
                    this.props.deleteScorecard(payload, after);
                },
            },
        });
    };

    handleAssign = (e) => {
        let { companyId, userId, page, perPage, dateStart, dateEnd, active } = this.state;

        if (companyId[0] < 0) companyId = this.props.companies.map((item) => item.id);
        if (userId[0] < 0) userId = null;

        const after =
            active !== 'employees'
                ? {
                      type: 'GETSAVEACTUALSREPORT_REQUEST',
                      payload: {
                          companyId,
                          dateStart,
                      },
                  }
                : {
                      type: 'COMPANYPERFORMANCEREVIEWS_REQUEST',
                      payload: {
                          companyId,
                          userId,
                          page,
                          perPage,
                          dateStart,
                          dateEnd,
                          order: 'name',
                          sort: 'ASC',
                      },
                  };

        const payload = {
            type: 'Assign ScoreCard',
            data: {
                before: {
                    assignees: [e.user.id],
                    modules: [],
                    disabled: ['daily', 'quarterly', 'yearly'],
                    user: e.user,
                    after,
                },
            },
        };
        this.props.toggleModal(payload);
    };

    handleRowClick = (item, type) => () => {
        this.handleSelectMenu(item)(type);
    };

    handleColumn = (e, page, per) => {
        let { column } = this.state;
        column.map((item, index) => keys(item).includes(e) && column.splice(index, 1));
        column.push({ [e]: { page, per } });
        this.setState({ column });
    };

    handleKeyChange = (key) => {
        const data = this.state.activeKey;
        data.includes(key) ? data.splice(data.indexOf(key), 1) : data.push(key);
        this.setState({ activeKey: data });
    };

    handleSortEmployees = (e) => {
        const { order, sort } = e;
        const { companyId, userId, page, perPage, dateStart, dateEnd } = this.state;
        this.handleFetch(true, companyId, userId, page, perPage, dateStart, dateEnd, order, sort);
    };

    handleReportMenu = () => {
        const { activeKey, companyId, dateStart, dateEnd, active, column, page, perPage } = this.state;
        const { actuals, reviews } = this.props;
        PerformanceReviewPdf({
            reviews,
            actuals,
            activeKey,
            companyId,
            dateStart,
            dateEnd,
            active,
            column,
            page,
            perPage,
        });
    };

    render() {
        const { companyId, dateStart, dateEnd, active } = this.state;
        const { reviews, actuals } = this.props;
        const date = moment.range(dateStart, dateEnd);
        const data = reviews;

        return (
            <ErrorBoundary className="vdmm-cp">
                <div className="page-filter">
                    <Filter defaultIds={[[], [-1]]} withAllEmployees onChange={this.handleFilter} />
                </div>
                <div className="card">
                    <div className="d-h-between">
                        <span className="dsl-b22 bold">{moment(dateStart).format('MMMM')} Reviews</span>
                        <div className="d-flex">
                            <DatePicker
                                calendar="month"
                                append="caret"
                                format="MMM YY"
                                as="span"
                                align="right"
                                value={date}
                                disabledDate={(e) => moment(e).isAfter(moment().startOf('month'))}
                                onSelect={this.handleDate}
                            />
                            <EditDropdown className="ms-3" dataCy="editDropdown" options={['Print']} onChange={this.handleReportMenu} />
                        </div>
                    </div>
                    <ResponsiveTabs active={active} data={ScorecardsTabs} getId={(e) => e.name} getValue={(e) => e.label} onTab={this.handleTab} />
                    <Tab eventKey="employees" active={active}>
                        <AverageScore className="mt-3" data={data?.stats} />
                        <EmployeeList
                            data={data}
                            dateStart={dateStart}
                            dateEnd={dateEnd}
                            pagination
                            onRowClick={this.handleRowClick}
                            onSelectMenu={this.handleSelectMenu}
                            onReport={this.handlePerformanceReport}
                            onScorecardDetail={this.handleDetail}
                            onPagination={this.handlePage}
                            onPer={this.handlePer}
                            onSort={this.handleSortEmployees}
                        />
                    </Tab>
                    <Tab eventKey="chart" active={active}>
                        <AverageScore className="mt-3" data={data?.stats} />
                        <OrgChartEmployeeReport
                            data={actuals}
                            companyId={companyId}
                            dateStart={dateStart}
                            dateEnd={dateEnd}
                            onColumn={this.handleColumn}
                            onRowClick={this.handleRowClick}
                            onReport={this.handlePerformanceReport}
                            onSelectMenu={this.handleSelectMenu}
                            onScorecardDetail={this.handleDetail}
                        />
                    </Tab>
                    <Tab eventKey="saveactuals" active={active}>
                        <AverageScore className="mt-3" data={data?.stats} />
                        <SaveActualEmployeeReport data={actuals} companyId={companyId} onKeyChange={this.handleKeyChange} />
                    </Tab>
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state) => ({
    isManager: state.app.app_role_id < 4,
    appId: state.app.id,
    companyId: state.app.selectedCompany,
    companies: state.app.companies,
    employees: state.app.users,
    reviews: state.manage.companyPerformanceReviews,
    actuals: state.manage.saveactualsReport,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPersonalReviews: (e, route) => dispatch(ManageActions.individualperformancereviewRequest(e, route)),
    fetchCompanyReviews: (payload, loading) => dispatch(ManageActions.companyperformancereviewsRequest(payload, loading)),
    fetchCompanyReviewsStats: (payload, loading) => dispatch(ManageActions.companyperformancereviewsstatsRequest(payload, loading)),
    deleteScorecard: (payload, after) => dispatch(ManageActions.postdeletescorecardRequest(payload, after)),
    getActualsReport: (payload, loading) => dispatch(ManageActions.getsaveactualsreportRequest(payload, loading)),
    toggleModal: (payload) => dispatch(AppActions.modalRequest(payload)),
    getUser: (payload, route) => dispatch(CommunityActions.getcommunityuserRequest(payload, null, route)),
});

const ConnectedScorecardList = connect(mapStateToProps, mapDispatchToProps)(ScorecardList);
const ScorecardListWithNavigation = withNavigate(ConnectedScorecardList);

export default ScorecardListWithNavigation;
